import { useQuery } from "@apollo/client"
import { useState } from "react"
import { Link, Routes, Route } from "react-router-dom"
import type { ColumnDef, CellContext } from "@tanstack/react-table"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { DataTable } from "~/ui/data-table"
import { useViewer } from "~/auth/use-viewer"
import { NEARBY_LOCATIONS_QUERY } from "~/plans/nearby-locations-query"
import { LocationDetailsScreen } from "./location-details-screen"
import { LocationNewScreen } from "./location-new-screen"
import type { NearbyLocationsQuery } from "~/__generated__/graphql"

// Extract the Location node type from the query result
type LocationNode = NonNullable<
  NonNullable<NonNullable<NearbyLocationsQuery["nearbyLocations"]>["edges"][0]>["node"]
>

// Define columns for the locations table
const columns: ColumnDef<LocationNode>[] = [
  {
    accessorKey: "name",
    header: "Name",
  },
  {
    accessorKey: "distance",
    header: "Distance (miles)",
    cell: ({ row }: CellContext<LocationNode, unknown>) => row.original?.distance?.toFixed(1) ?? "",
  },
  {
    accessorKey: "city",
    header: "City",
  },
  {
    accessorKey: "stateProvince",
    header: "State/Province",
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }: CellContext<LocationNode, unknown>) => (
      <div className="flex gap-2">
        <Link to={`/locations/${row.original?.id}`}>
          <Button variant="outline" size="sm">
            View
          </Button>
        </Link>
      </div>
    ),
  },
]

export const UserLocationsScreen = () => {
  const { viewer } = useViewer()
  const [distance, setDistance] = useState(25) // Default 25 miles radius

  const { data, loading, error } = useQuery(NEARBY_LOCATIONS_QUERY, {
    variables: {
      // Provide default 0 only to satisfy TS when skipped; actual values used when query runs.
      latitude: viewer?.latitude ?? 0,
      longitude: viewer?.longitude ?? 0,
      distance: distance,
    },
    skip: !viewer?.latitude || !viewer?.longitude,
  })

  if (error) {
    return <GraphqlError error={error} />
  }

  // Filter out null/undefined nodes just in case, satisfying the DataTable type
  const locations: LocationNode[] =
    data?.nearbyLocations?.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is LocationNode => node != null) ?? []

  return (
    <Routes>
      <Route path="new" element={<LocationNewScreen />} />
      <Route path=":id" element={<LocationDetailsScreen />} />
      <Route
        index
        element={
          <TablePageLayout>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl">Nearby Locations</h1>
                <p className="text-sm text-gray-500">
                  Showing locations within {distance} miles of your location
                </p>
              </div>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <label htmlFor="distance" className="text-sm">
                    Distance:
                  </label>
                  <select
                    id="distance"
                    className="rounded border border-gray-300 px-2 py-1"
                    value={distance}
                    onChange={(e) => setDistance(Number(e.target.value))}
                  >
                    <option value={5}>5 miles</option>
                    <option value={10}>10 miles</option>
                    <option value={25}>25 miles</option>
                    <option value={50}>50 miles</option>
                    <option value={100}>100 miles</option>
                  </select>
                </div>
                <Link to="/locations/new">
                  <Button variant="outline">Suggest a Location</Button>
                </Link>
              </div>
            </div>
            <hr className="my-4 border-0 border-b" />

            {!viewer?.latitude || !viewer?.longitude ? (
              <div className="rounded-md bg-yellow-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Location needed</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        Please update your profile with your location to see nearby game locations.
                      </p>
                    </div>
                    <div className="mt-4">
                      <div className="-mx-2 -my-1.5 flex">
                        <Link
                          to="/profile/edit"
                          className="rounded-md bg-yellow-50 px-2 py-1.5 text-sm font-medium text-yellow-800 hover:bg-yellow-100"
                        >
                          Update Profile
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : loading ? (
              <div className="py-8 text-center">Loading nearby locations...</div>
            ) : locations.length === 0 ? (
              <div className="rounded-md bg-blue-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3 flex flex-1 justify-between">
                    <div className="text-sm text-blue-700">
                      <p>No locations found within {distance} miles of your location.</p>
                    </div>
                    <div className="ml-6 flex-shrink-0">
                      <Link
                        to="/locations/new"
                        className="whitespace-nowrap text-sm font-medium text-blue-700 hover:text-blue-600"
                      >
                        Suggest a new location
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <DataTable columns={columns} data={locations} />
            )}
          </TablePageLayout>
        }
      />
    </Routes>
  )
}
